import React, { useState, useRef } from "react";
import { Upload, X, Star, Loader2 } from "lucide-react";
import { api, BACKEND_URL } from "../lib/shop";
import { toast } from "sonner";

/**
 * Multi-image uploader with primary selection, reordering and remove.
 * Stores image URLs (absolute or path-relative `/api/files/...`).
 * Props:
 *   value: string[] – current list of image URLs (first = primary)
 *   onChange: (newList: string[]) => void
 */
export default function ImageUploader({ value = [], onChange }) {
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    const uploaded = [];
    try {
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        const absUrl = data.url.startsWith("http") ? data.url : `${BACKEND_URL}${data.url}`;
        uploaded.push(absUrl);
      }
      onChange([...(value || []), ...uploaded]);
      toast.success(`${uploaded.length} ${uploaded.length === 1 ? "imagine adăugată" : "imagini adăugate"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Eroare la upload");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onPaste = async (e) => {
    const items = Array.from(e.clipboardData?.items || []);
    const imgs = items.filter((i) => i.type.startsWith("image/")).map((i) => i.getAsFile());
    if (imgs.length) {
      e.preventDefault();
      await upload(imgs);
    }
  };

  const setPrimary = (idx) => {
    if (idx === 0) return;
    const next = [...value];
    const [picked] = next.splice(idx, 1);
    next.unshift(picked);
    onChange(next);
  };
  const removeAt = (idx) => onChange(value.filter((_, i) => i !== idx));
  const addUrl = () => {
    const url = window.prompt("Lipește URL-ul imaginii (https://...)");
    if (url && url.startsWith("http")) onChange([...(value || []), url.trim()]);
  };

  return (
    <div className="space-y-3" data-testid="image-uploader" onPaste={onPaste}>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {(value || []).map((url, idx) => (
          <div key={idx} className={`relative aspect-square border ${idx === 0 ? 'border-[#1ebe57] border-2' : 'border-gray-300'} bg-gray-50 overflow-hidden group`} data-testid={`uploader-img-${idx}`}>
            <img src={url} alt="" className="w-full h-full object-contain p-2" />
            {idx === 0 && (
              <span className="absolute top-1 left-1 bg-[#1ebe57] text-white text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 inline-flex items-center gap-1">
                <Star size={9} fill="white" /> Principală
              </span>
            )}
            <div className="absolute inset-0 bg-gray-900/0 group-hover:bg-gray-900/40 transition flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              {idx !== 0 && (
                <button type="button" onClick={() => setPrimary(idx)} className="px-2 py-1 bg-white text-xs font-bold uppercase tracking-wider hover:bg-[#1ebe57] hover:text-white" data-testid={`set-primary-${idx}`}>
                  Pune principală
                </button>
              )}
              <button type="button" onClick={() => removeAt(idx)} className="p-1.5 bg-white hover:bg-red-600 hover:text-white" data-testid={`remove-img-${idx}`}>
                <X size={14} />
              </button>
            </div>
          </div>
        ))}

        <label className="aspect-square border border-dashed border-gray-300 bg-white hover:border-gray-900 cursor-pointer flex flex-col items-center justify-center gap-2 text-gray-500 hover:text-gray-900" data-testid="add-image-zone">
          {uploading ? <Loader2 size={22} className="animate-spin" /> : <Upload size={22} strokeWidth={1.5} />}
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-center px-2">{uploading ? "Se încarcă..." : "Adaugă imagine"}</span>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={(e) => upload(e.target.files)}
            data-testid="file-input"
          />
        </label>
      </div>
      <div className="flex items-center justify-between text-[11px] text-gray-500">
        <span>Primul = imaginea principală · click pe altul pentru a-l face principal · poți și lipi (Ctrl+V) o imagine copiată</span>
        <button type="button" onClick={addUrl} className="underline-offset-2 hover:underline hover:text-gray-900" data-testid="add-url-btn">+ adaugă URL extern</button>
      </div>
    </div>
  );
}
